import { Badge } from "@hortis/ui/badge";
import { colors } from "@hortis/ui/colors";
import { A, O, pipe } from "@mobily/ts-belt";
import * as turf from "@turf/turf";
import Color from "color";
import type { BBox, Feature, Point } from "geojson";
import type { MapGeoJSONFeature } from "maplibre-gl";
import { useEffect, useRef, useState } from "react";
import { Layer, Marker, Source } from "react-map-gl";
import { useHoveredListLocationStore } from "src/features/collection-map/components/hovered-list-location-store";
import type { MapPositioningLocation } from "src/features/collection-map/components/types";
import { useMapRouteMatch } from "src/features/collection-map/map-store";
import { mapId } from "src/features/collection-map/utils";
import { truncateString } from "src/utils/truncate-string";
import { twMerge } from "tailwind-merge";
import { useMaplibre } from "../utils";
import {
  type LocationProperties,
  type LocationsGeometry,
  locationPropertiesSchema,
} from "./parse-geometry";
import("mapbox-gl/dist/mapbox-gl.css");

interface LocationPolygonViewingLayerProps {
  color?: GeometryColor;
  geometry: LocationsGeometry | null;
  editMode?: boolean;
  currentHoveredFeature?: MapGeoJSONFeature | undefined;
  editingLocation?: MapPositioningLocation | null;
}

export type GeometryColor = "primary" | "warning" | "error" | "blue" | "purple";
const GeometryColors: Record<GeometryColor, string> = {
  primary: colors.primary[400],
  warning: colors.warning[400],
  error: colors.error[400],
  blue: "#42A5F5",
  purple: "#9B8AFB",
};

const lightenColor = (color: string) => {
  return Color(color).saturate(0.4).hex();
};
const selectedColor = (color: string) => {
  return Color(color).lighten(0.1).saturate(0.2).hex();
};

function LocationNameMarker({
  overrideColor,
  feature,
}: {
  feature: Feature<Point, LocationProperties & { bounds: BBox }>;
  overrideColor: string;
}) {
  const [isVisible, setIsVisible] = useState(true);
  const markerRef = useRef<HTMLDivElement | null>(null);
  const { [mapId]: map } = useMaplibre();
  const bounds = feature.properties.bounds;

  useEffect(() => {
    function checkMarkerVisibility() {
      if (markerRef.current == null || map == null) {
        return;
      }

      const markerRect = markerRef.current.getBoundingClientRect();
      const [minLng, minLat, maxLng, maxLat] = bounds;

      // Convert bounds coordinates to pixel coordinates
      const topLeft = map.project([minLng, maxLat]);
      const bottomRight = map.project([maxLng, minLat]);

      // Calculate the width and height of the box
      const boundingBoxWidth = bottomRight.x - topLeft.x;
      const boundingBoxHeight = bottomRight.y - topLeft.y;

      const markerWidth = markerRect.right - markerRect.left;
      const markerHeight = markerRect.bottom - markerRect.top;

      // Check if the marker is within the bounds
      const isWithinBounds =
        boundingBoxHeight > markerHeight && boundingBoxWidth > markerWidth;

      setIsVisible(isWithinBounds);
    }

    checkMarkerVisibility();
    map?.on("move", checkMarkerVisibility);
    map?.on("zoom", checkMarkerVisibility);

    return () => {
      map?.off("move", checkMarkerVisibility);
      map?.off("zoom", checkMarkerVisibility);
    };
  }, [markerRef, bounds, map]);

  return (
    <Marker
      key={feature.id}
      longitude={feature.geometry.coordinates[0]}
      latitude={feature.geometry.coordinates[1]}
    >
      <Badge
        ref={markerRef}
        variant="solid"
        style={{
          backgroundColor: overrideColor,
          cursor: "pointer",
        }}
        className={twMerge(isVisible ? "visible" : "invisible")}
      >
        {feature.properties.name}
      </Badge>
    </Marker>
  );
}

const LocationPolygonViewingLayer = ({
  color: inputcolor = "primary",
  geometry,
  editMode,
  currentHoveredFeature,
  editingLocation,
}: LocationPolygonViewingLayerProps) => {
  const { hoveredListLocation } = useHoveredListLocationStore();
  const { [mapId]: map } = useMaplibre();
  const matchedRoute = useMapRouteMatch();
  const selectedLocationName =
    matchedRoute.route === "location" ? matchedRoute.name : null;
  if (!geometry) {
    return null;
  }

  const overrideColor = GeometryColors[inputcolor];
  const color = hoveredListLocation?.color ?? overrideColor;

  const selectedLocationColor = selectedColor(color);
  const selectedLocationHoverColor = lightenColor(selectedLocationColor);

  return (
    <>
      <Source id="location-drawing" type="geojson" data={geometry} generateId>
        <Layer
          source="location-drawing"
          beforeId={
            // eslint-disable-next-line sonarjs/no-duplicate-string
            map?.getLayer("unclustered-point") == null
              ? undefined
              : "unclustered-point"
          }
          id="location-polygon-fill"
          type="fill"
          filter={["all"]}
          paint={{
            "fill-color": [
              "case",
              [
                "any",
                // eslint-disable-next-line sonarjs/no-duplicate-string
                ["boolean", ["feature-state", "hover"], false],
                ["==", ["get", "id"], hoveredListLocation?.id ?? null],
              ],
              [
                // if hovered
                "case",
                ["==", ["get", "name"], selectedLocationName],
                selectedLocationHoverColor, // if selected
                lightenColor(color), // if not selected
              ],
              [
                // if not hovered
                "case",
                ["==", ["get", "name"], selectedLocationName],
                selectedLocationColor, // if selected
                color, // if not selected
              ],
            ],
            "fill-outline-color": [
              "case",
              [
                "any",
                ["boolean", ["feature-state", "hover"], false],
                ["==", ["get", "id"], hoveredListLocation?.id ?? null],
              ],
              [
                "case",
                ["==", ["get", "name"], selectedLocationName],
                selectedLocationHoverColor,
                lightenColor(color),
              ],
              [
                "case",
                ["==", ["get", "name"], selectedLocationName],
                selectedLocationColor, // if selected
                color, // if not selected
              ],
            ],
            "fill-opacity": editMode === true ? 0.1 : 0.12,
          }}
        />
        <Layer
          beforeId={
            map?.getLayer("unclustered-point") == null
              ? undefined
              : "unclustered-point"
          }
          source="location-drawing"
          id="location-polygon-line"
          type="line"
          filter={["all"]}
          layout={{
            "line-cap": "round",
            "line-join": "round",
          }}
          paint={{
            "line-color": [
              "case",
              [
                "any",
                ["boolean", ["feature-state", "hover"], false],
                ["==", ["get", "id"], hoveredListLocation?.id ?? null],
              ],
              [
                "case",
                ["==", ["get", "name"], selectedLocationName],
                selectedLocationHoverColor,
                color,
              ],
              [
                "case",
                ["==", ["get", "name"], selectedLocationName],
                selectedLocationColor,
                color,
              ],
            ],
            "line-width": 2,
            "line-opacity": editMode === true ? 0.3 : 0.6,
          }}
        />
      </Source>

      {geometry.features
        .flatMap((f) =>
          pipe(
            turf.flatten(f.geometry).features,
            A.map((c) =>
              turf.centerOfMass(c, {
                properties: {
                  ...f.properties,
                  bounds: turf.bbox(c),
                  name: truncateString(f.properties.name, 10),
                },
              }),
            ),
          ),
        )
        .map((p) => {
          const isHovered = pipe(
            currentHoveredFeature,
            O.fromNullable,
            O.filter(() => editMode === false),
            O.filter(
              (feature) =>
                feature.geometry.type === "MultiPolygon" ||
                feature.geometry.type === "Polygon",
            ),
            O.filter((feature) => {
              const result = locationPropertiesSchema.safeParse(
                feature.properties,
              );
              return result.success
                ? result.data.id === p.properties.id
                : false;
            }),
            O.isSome,
          );

          const isSelected =
            // eslint-disable-next-line @typescript-eslint/prefer-string-starts-ends-with
            p.properties.name.slice(0, 10) ===
            selectedLocationName?.slice(0, 10);
          return (
            <LocationNameMarker
              key={p.geometry.coordinates.join(",")}
              feature={p}
              overrideColor={
                editMode === false
                  ? isHovered
                    ? isSelected
                      ? selectedLocationHoverColor
                      : lightenColor(color)
                    : isSelected
                      ? selectedLocationColor
                      : color
                  : editingLocation?.id === p.properties.id
                    ? colors.warning[400]
                    : (p.properties.color ?? colors.primary[400])
              }
            />
          );
        })}
    </>
  );
};

export { LocationPolygonViewingLayer };
